import React, { useEffect, useState } from "react";
import {
	AssetRepo,
	FeesRepo,
	VendorRepo,
	ApplicationRepo,
} from "../../repositories";
import useAPIError from "../../common/lib/APIError";
import NmefSpinner from "../../common/lib/NmefSpinner";
import AssetContext from "../../common/lib/Assets/AssetContext";
import Asset from "../../common/lib/Assets";
import { AssetDataAPI } from "../../common/lib/API/AssetDataAPI";
import { DocsDataAPI } from '../../common/lib/API/DocsDataAPI';
import { ApplicationDataAPI } from "../../common/lib/API/ApplicationDataAPI";

const Assets = ({ app, isReadOnly, setAppTotalData, appTotalData  }) => {
	const { addError, setLoader } = useAPIError();
	const [assets, setAssets] = useState([]);
	const [assetDocuments, setAssetDocuments] = useState([]);
	const [fees, setFees] = useState([]);
	const [vendors, setVendors] = useState([]);
	const [locations, setLocations] = useState([]);
	const [stateSalesTax, setStateSalesTax] = useState({});
	const [usStates, setUsStates] = useState([]);
	const [assetTypes, setAssetTypes] = useState([]);
	const [addAssetModalOpen, setAddAssetModalOpen] = useState(false);
	const [isLoading, setLoading] = useState(false);
	const [isRefresh, setIsRefresh] = useState(false);
	const [feeTypes, setFeeTypeList] = useState([]);

	useEffect(() => {
		(async () => {
			if (app) {
				setLoading(true);
				try {
					const response = await Promise.allSettled([
						AssetRepo.getApplicationAssetsById(app),
						FeesRepo.getApplicationFeesById(app),
						FeesRepo.getApplicationStateSalesTaxById(app),
						VendorRepo.getApplicationLocationsById(app),
						VendorRepo.getUsStates(),
						AssetRepo.getAssetTypes({ active: true }),
						VendorRepo.getAllVendors(0),
						ApplicationRepo.getApplicationDocuments(app),
					]);

					// console.log("Asset reponse: ", response);

					const assetResponse =
						response[0].status === "fulfilled" ? response[0].value : [];
					const feesResponse =
						response[1].status === "fulfilled" ? response[1].value : [];
					const appSalesTaxResponse =
						response[2].status === "fulfilled" ? response[2].value : [];
					const appLocationResponse =
						response[3].status === "fulfilled" ? response[3].value : [];
					const usStateResponse =
						response[4].status === "fulfilled" ? response[4].value : {};
					const assetTypesResponse =
						response[5].status === "fulfilled" ? response[5].value : [];
					const vendorResponse =
						response[6].status === "fulfilled" ? response[6].value : [];
					const documentResponse =
						response[7].status === "fulfilled"
							? response[7].value?.filter(
								(x) => x.type === "Asset - Evidence" && x.asset !== undefined
							)
							: [];

					setAssets(assetResponse);
					setAssetDocuments(documentResponse);
					setFees(feesResponse);
					if (appSalesTaxResponse && appSalesTaxResponse.length)
						setStateSalesTax(appSalesTaxResponse[0]);
					setLocations(appLocationResponse);
					setUsStates(usStateResponse);
					setAssetTypes(assetTypesResponse);
					setVendors(vendorResponse);

					const errors = [];
					response.map((item) => {
						if (item.status !== "fulfilled") errors.push(item.reason.message);
						return item;
					});

					if (errors.length > 0) addError(errors);
				} catch (error) {
					addError(error.message);
				} finally {
					setLoading(false);
					setIsRefresh(false);
				}
			}
		})();
	}, [app, isRefresh, addError]);

	const onAssetAdd = async (data) => {
		try {
			setLoader(true);
			await AssetRepo.addAsset(data);
			const assetsData = await AssetRepo.getApplicationAssetsById(app);
			// setAssets([...assets, { ...data, id: assetId }]);
			setAssets(assetsData);
		} catch (error) {
			addError(error.message);
		} finally {
			setLoader(false);
		}
	};

	
	const refreshApplicationData = async () => {
		if (!app?.id) {
			return;
		}
		try {
			const brokerId = app?.broker_id ?? appTotalData?.broker_id ?? "ALL";
			const email = app?.locked_by_email ?? appTotalData?.locked_by_email;
			if (!email) {
				return;
			}
			const updatedApplication = await ApplicationDataAPI.fetchApplicationById(
				app.id,
				brokerId,
				email
			);
			if (updatedApplication && typeof setAppTotalData === "function") {
				setAppTotalData(updatedApplication);
			}
		} catch (error) {
			addError(error.message);
		}
	};

	const onAssetUpdate = async (data, attachment) => {
		try {
			setLoader(true);
			setAssets(
				assets.map((item) => (item.id === data.id ? { ...item, ...data } : item))
			);
			// #18911 check for nulls.
			if (attachment  && attachment.name) {
				console.log("xyz attachment", attachment)
				await DocsDataAPI.uploadDocumentToFileStore(
					app.id,
					app.broker_id,
					attachment,
					"Asset - Evidence",
					null,
					app.locked_by_name,
					data.id
				);
			}
			if (data.information_source === undefined || data.information_source == "") {
				data.information_source = "Truck Blue Book";
			}
			let isIncomplete = data?.is_incomplete;
			if (data?.is_incomplete) {
				if (data.is_trade_in || data.is_collateral) {
					isIncomplete = false;
				} else if (
					!data.override_year ||
					!data.override_make ||
					!data.override_model
				)
					isIncomplete = true;
				else if (
					data.override_hours == 0 &&
					data.override_mileage == 0 &&
					data.primary_condition == "Used"
				) {
					isIncomplete = true;
				} else {
					isIncomplete = false;
				}
			}
			data.is_incomplete = isIncomplete;
			//these fields are removed otherwise updateAsset API fails
			delete data.inspection_location_address;
			delete data.override_asset_type_gps_fee;
			delete data.override_asset_type_group;
			delete data.override_asset_type_risk_group;
			delete data.override_truck_body_wholesale_price;
			delete data.Asset_Location_Address;
			delete data.vendor;
			delete data.master_vendor;
			delete data.primary_year;
			await AssetRepo.updateAsset(data);			
			await refreshApplicationData();
			setIsRefresh(true);
		} catch (error) {
			addError(error.message);
		} finally {
			setLoader(false);
		}
	};

	const onChangeAsset = async (data, attachment) => {
		try {
			setLoader(true);
			setAssets(
				assets.map((item) => (item.id === data.id ? { ...item, ...data } : item))
			);
			if (data.is_location == false || data.is_location == undefined) {
				data.vendor = data.vendor_id;
			} else delete data.vendor;
			delete data.is_location;
			delete data.vendor_id;
			// #18911 check for nulls.
			if (attachment && attachment.name != "") {
				console.log("abc attachment", attachment)

				await DocsDataAPI.uploadDocumentToFileStore(
					app.id,
					app.broker_id,
					attachment,
					"Asset - Evidence",
					null,
					app.locked_by_name
				);
			}
			if (data.information_source === undefined || data.information_source == "") {
				data.information_source = "Truck Blue Book";
			}
			//these fields are removed otherwise updateAsset API fails
			delete data.override_asset_type_gps_fee;
			delete data.override_asset_type_group;
			delete data.override_asset_type_risk_group;
			delete data.override_truck_body_wholesale_price;
			delete data.Asset_Location_Address;
			delete data.master_vendor;
			delete data.primary_year;
			await AssetRepo.updateAsset(data);
			await refreshApplicationData();
			setIsRefresh(true);
		} catch (error) {
			addError(error.message);
		}
		finally {
			setLoader(false);
		}
	};

	// const DocumentUpload = async (data) => {
	// 	try {
	// 		let DocumentId = "";
	// 		if (Object.keys(data)?.length === 0) {
	// 			return;
	// 		}
	// 		if (data?.application_id === undefined || data?.application_id === null) {
	// 			return;
	// 		}
	// 		if (Object.keys(data)?.length !== 0) {
	// 			var now = new Date();
	// 			var isoString = now.toISOString();
	// 			const documentInfo = {
	// 				application_id: data.application_id,
	// 				asset: data.asset,
	// 				document_name: data.document_name,
	// 				date_uploaded: isoString,
	// 				type: data.type,
	// 				related_to: "Lessee",
	// 				notes: "Test",
	// 			};
	// 			let documentUp = await ApplicationRepo.postDocument(
	// 				documentInfo,
	// 				app?.broker_id
	// 			);

	// 			const attachmentInfo = {
	// 				name: data.document_name,
	// 				parent_id: documentUp,
	// 				content_type: data.content_type,
	// 				body: data.base_file,
	// 				private: false,
	// 			};
	// 			await ApplicationRepo.postAttachment(attachmentInfo);
	// 			DocumentId = documentUp;
	// 		}
	// 		return DocumentId;
	// 	} catch (error) {
	// 		addError(error.message);
	// 	}
	// };

	const updateAssets = (newAssets) => {
		setAssets(newAssets);
	};
	const onAssetDuplicate = async (data) => {
		try {
			setLoader(true);

			if (data.information_source === undefined || data.information_source == "") {
				data.information_source = "Truck Blue Book";
			}
			let isIncomplete = data?.is_incomplete;
			if (data?.is_incomplete) {
				if (data.is_trade_in || data.is_collateral) {
					isIncomplete = false;
				} else if (
					!data.override_year ||
					!data.override_make ||
					!data.override_model
				)
					isIncomplete = true;
				else if (
					data.override_hours == 0 &&
					data.override_mileage == 0 &&
					data.primary_condition == "Used"
				) {
					isIncomplete = true;
				} else {
					isIncomplete = false;
				}
			}
			data.is_incomplete = isIncomplete;
			//these fields are removed otherwise updateAsset API fails
			delete data.inspection_location_address;
			delete data.override_asset_type_gps_fee;
			delete data.override_asset_type_group;
			delete data.override_asset_type_risk_group;
			delete data.override_truck_body_wholesale_price;
			delete data.Asset_Location_Address;
			delete data.vendor;
			delete data.master_vendor;
			delete data.primary_year;
			await AssetDataAPI.addAsset(data);
			setIsRefresh(true);
		} catch (error) {
			addError(error.message);
		} finally {
			setLoader(false);
		}
	};

	const onAssetDelete = async (assetId) => {
		try {
			setLoader(true);
			await AssetRepo.deleteAsset(assetId);
			setAssets(assets.filter((asset) => asset.id !== assetId));
		} catch (error) {
			addError(error.message);
		} finally {
			setLoader(false);
		}
	};
	const getAssetDocuments = async (assetId) => {
		try {
			setLoader(true);
			await AssetRepo.getAssetDocuments(assetId);
			setAssets(assets.filter((asset) => asset.id !== assetId));
		} catch (error) {
			addError(error.message);
		} finally {
			setLoader(false);
		}
	};

	const onFeeUpdate = async (data) => {
		try {
			setLoader(true);
			setFees(
				fees.map((item) => (item.id === data.id ? { ...item, ...data } : item))
			);
			await FeesRepo.updateFee(data);
			setIsRefresh(true);
		} catch (error) {
			addError(error.message);
		}
		finally {
			setLoader(false);
		}
	};

	const onFeeAdd = async (data) => {
		try {
			setLoader(true);
			await FeesRepo.addFee(data);
			//setFees([...fees, { ...data, id: feeId }]);
			const fees = await FeesRepo.getApplicationFeesById(app);
			setFees(fees);
		} catch (error) {
			addError(error.message);
		} finally {
			setLoader(false);
		}
	};

	const onFeeDelete = async (feeId) => {
		try {
			setLoader(true);
			await FeesRepo.deleteFee(feeId);
			setFees(fees.filter((fee) => fee.id !== feeId));
		} catch (error) {
			addError(error.message);
		} finally {
			setLoader(false);
		}
	};

	const onVendorAdd = async (data) => {
		try {
			setLoader(true);
			const vendorId = await VendorRepo.addVendor(data);
			setVendors([...vendors, { ...data, id: vendorId }]);
		} catch (error) {
			addError(error.message);
		} finally {
			setLoader(false);
		}
	};

	const getVendors = async (name, offset) => {
		const response = await VendorRepo.getVendors(name, offset);
		return response;
	};
	const getAttachment = async (documentId) => {
		try {
			let Attachment = await ApplicationRepo.getDocumentInfo(documentId);
			return Attachment;
		} catch (error) {
			console.log(error.message);
		}
	};

	const getVendor = async (data) => {
		const response = await VendorRepo.getVendor(data);
		return response;
	};

	const getPDConfig = async (data) => {
		const response = await AssetRepo.getPriceDigestConfig(data);
		return response;
	};

	const getPDValue = async (data) => {
		const response = await AssetRepo.getPriceDigestValue(data);
		return response;
	};

	const getEWConfig = async (data) => {
		const response = await AssetRepo.getEquipmentWatchConfig(data);
		return response;
	};

	const getEWValue = async (data) => {
		const response = await AssetRepo.getEquipmentWatchValue(data);
		return response;
	};

	return (
		<AssetContext.Provider
			value={{
				isReadOnly: isReadOnly,
				app: app,
				assets: assets,
				assetDocuments: assetDocuments,
				fees: fees,
				vendors: vendors,
				locations: locations,
				stateSalesTax: stateSalesTax,
				usStates: usStates,
				assetTypes: assetTypes,
				feeTypes: feeTypes,
				onAssetAdd,
				onAssetUpdate,
				onChangeAsset,
				onAssetDelete,
				onFeeUpdate,
				onFeeAdd,
				onFeeDelete,
				onVendorAdd,
				getVendor,
				getVendors,
				getPDConfig,
				getPDValue,
				getEWConfig,
				getEWValue,
				getAttachment,
				getAssetDocuments,
				updateAssets,
				onAssetDuplicate,
				setFeeTypeList,
				setIsRefresh,
			}}>
			{isLoading ? (
				<div style={{ padding: 20 }}>
					<NmefSpinner />
				</div>
			) : (
				!isRefresh && (
					<>
						<Asset
							addAssetModalOpen={addAssetModalOpen}
							setAddAssetModalOpen={setAddAssetModalOpen}
						/>
					</>
				)
			)}
		</AssetContext.Provider>
	);
};
export default Assets;
